import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import Seo from "@/components/seo/Seo";

export default function ForEmployersThankYou() {
  const [params] = useSearchParams();
  const inquiryId = params.get("id");
  const total = params.get("total");
  const perEmployee = params.get("per_employee");
  const pkg = params.get("pkg");

  return (
    <>
      <Seo
        title="Thanks — we've got your inquiry | Safe to Spend 365 for Business"
        description="Your Safe to Spend 365 for Business inquiry has been received. We'll be in touch within one business day."
        path="/for-employers/thank-you"
        noindex
      />

      <section className="relative">
        <div className="mx-auto max-w-2xl px-6 lg:px-10 pt-20 pb-16 md:pt-28 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0A1128] text-white">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1
            data-testid="thanks-title"
            className="mt-6 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#0A1128]"
          >
            Thanks — we've got it.
          </h1>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
            Your inquiry is in our queue. Someone from our team will review the
            details and reach out within one business day with next steps.
          </p>

          {(total || perEmployee || pkg) && (
            <div
              data-testid="thanks-estimate"
              className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-left"
            >
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Preliminary estimate
              </p>
              <div className="mt-3 grid sm:grid-cols-3 gap-4">
                {pkg && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">
                      Package
                    </p>
                    <p className="mt-1 text-[15px] font-medium text-[#0A1128] capitalize">
                      {pkg}
                    </p>
                  </div>
                )}
                {perEmployee && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">
                      Per employee
                    </p>
                    <p className="mt-1 text-[15px] font-medium text-[#0A1128]">
                      {perEmployee}
                    </p>
                  </div>
                )}
                {total && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">
                      Estimated total
                    </p>
                    <p className="mt-1 text-[15px] font-medium text-[#0A1128]">
                      {total}
                    </p>
                  </div>
                )}
              </div>
              <p className="mt-4 text-[11.5px] text-slate-400">
                Final invoice confirmed after admin approval.
              </p>
            </div>
          )}

          <div className="mt-10 grid sm:grid-cols-3 gap-5 text-left">
            <NextStep
              icon={<Mail className="h-4 w-4" />}
              title="Check your inbox"
              body="We sent you a confirmation email. Add customersupport@atlasforgefinancial.com to your contacts."
            />
            <NextStep
              icon={<Clock className="h-4 w-4" />}
              title="One business day"
              body="Our team will review your inquiry and reach out with a tailored quote."
            />
            <NextStep
              icon={<ShieldCheck className="h-4 w-4" />}
              title="Stripe invoice"
              body="On approval, you'll receive a secure invoice. Master code is issued the moment payment clears."
            />
          </div>

          {inquiryId && (
            <p
              data-testid="thanks-inquiry-id"
              className="mt-8 text-[11.5px] text-slate-400"
            >
              Reference:{" "}
              <span className="font-mono text-slate-500">{inquiryId}</span>
            </p>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              data-testid="thanks-home-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-5 py-2.5 text-[14px] font-medium hover:bg-[#1E293B] transition-colors"
            >
              Back to home <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/for-employers"
              data-testid="thanks-employers-cta"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-[14px] font-medium text-[#0A1128] hover:border-slate-400 transition-colors"
            >
              Review the program
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function NextStep({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#0A1128] text-white">
        {icon}
      </div>
      <h3 className="mt-3 font-display text-[15px] text-[#0A1128]">{title}</h3>
      <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">
        {body}
      </p>
    </div>
  );
}
